import { useI18n } from "#hooks/use-i18n"
import type { ImportMapping } from "#lib/indexeddb-import-store"
import { X } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"

const NONE_VALUE = "__none__"

interface ColumnMappingStepProps {
  headers: string[]
  mapping: ImportMapping
  onMappingChange: (field: keyof ImportMapping, value: string | string[]) => void
}

export function ColumnMappingStep({ headers, mapping, onMappingChange }: ColumnMappingStepProps) {
  const { t } = useI18n()
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{t("components.expenseImport.columnMapping")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("components.expenseImport.selectDateColumn")} *</Label>
            <Select
              value={Array.isArray(mapping.date) ? mapping.date[0] : mapping.date || ""}
              onValueChange={(value) => onMappingChange("date", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("components.expenseImport.selectDateColumn")} />
              </SelectTrigger>
              <SelectContent>
                {headers.map((header) => (
                  <SelectItem key={header} value={header}>
                    {header}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("components.expenseImport.selectNameColumn")} *</Label>
            <div className="space-y-2">
              <Select
                value={Array.isArray(mapping.name) ? mapping.name[0] : mapping.name || ""}
                onValueChange={(value) => onMappingChange("name", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("components.expenseImport.selectNameColumn")} />
                </SelectTrigger>
                <SelectContent>
                  {headers.map((header) => (
                    <SelectItem key={header} value={header}>
                      {header}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mapping.name && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="name-multi"
                    checked={Array.isArray(mapping.name)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onMappingChange("name", [
                          Array.isArray(mapping.name) ? mapping.name[0] : mapping.name || "",
                        ])
                      } else {
                        onMappingChange("name", Array.isArray(mapping.name) ? mapping.name[0] : "")
                      }
                    }}
                  />
                  <Label htmlFor="name-multi" className="text-xs font-normal">
                    {t("components.expenseImport.useMultipleColumns")}
                  </Label>
                </div>
              )}
              {Array.isArray(mapping.name) && (
                <div className="space-y-2 pl-6">
                  <Label className="text-xs">Additional columns:</Label>
                  {mapping.name.slice(1).map((col, idx) => {
                    const currentValue = col || NONE_VALUE
                    const availableHeaders = headers.filter((h) => {
                      if (h === col) return true
                      return !mapping.name?.includes(h)
                    })

                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <Select
                          value={currentValue}
                          onValueChange={(value) => {
                            const newCols = [...(mapping.name as string[])]
                            newCols[idx + 1] = value === NONE_VALUE ? "" : value
                            onMappingChange("name", newCols)
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t("components.expenseImport.selectColumn")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_VALUE}>
                              {t("components.expenseImport.selectColumn")}
                              ...
                            </SelectItem>
                            {availableHeaders.map((header) => (
                              <SelectItem key={header} value={header}>
                                {header}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0"
                          onClick={() => {
                            const newCols = [...(mapping.name as string[])]
                            newCols.splice(idx + 1, 1)
                            onMappingChange("name", newCols)
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newCols = [
                        ...(Array.isArray(mapping.name) ? mapping.name : [mapping.name || ""]),
                        "",
                      ]
                      onMappingChange("name", newCols)
                    }}
                  >
                    + {t("components.expenseImport.addColumn")}
                  </Button>
                  <div className="space-y-2">
                    <Label className="text-xs">{t("components.expenseImport.separator")}</Label>
                    <Input
                      value={mapping.nameSeparator || " "}
                      onChange={(e) => onMappingChange("nameSeparator", e.target.value)}
                      placeholder={t("components.expenseImport.separator")}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("components.expenseImport.selectDescriptionColumn")}</Label>
            <div className="space-y-2">
              <Select
                value={
                  Array.isArray(mapping.description)
                    ? mapping.description[0] || NONE_VALUE
                    : mapping.description || NONE_VALUE
                }
                onValueChange={(value) => onMappingChange("description", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={t("components.expenseImport.selectDescriptionColumn")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>{t("common.none")}</SelectItem>
                  {headers.map((header) => (
                    <SelectItem key={header} value={header}>
                      {header}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mapping.description && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="desc-multi"
                    checked={Array.isArray(mapping.description)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onMappingChange("description", [
                          Array.isArray(mapping.description)
                            ? mapping.description[0]
                            : mapping.description || "",
                        ])
                      } else {
                        onMappingChange(
                          "description",
                          Array.isArray(mapping.description) ? mapping.description[0] : ""
                        )
                      }
                    }}
                  />
                  <Label htmlFor="desc-multi" className="text-xs font-normal">
                    {t("components.expenseImport.useMultipleColumns")}
                  </Label>
                </div>
              )}
              {Array.isArray(mapping.description) && (
                <div className="space-y-2 pl-6">
                  <Label className="text-xs">
                    {t("components.expenseImport.additionalColumns")}:
                  </Label>
                  {mapping.description.slice(1).map((col, idx) => {
                    const currentValue = col || NONE_VALUE
                    const availableHeaders = headers.filter((h) => {
                      if (h === col) return true
                      return !mapping.description?.includes(h)
                    })

                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <Select
                          value={currentValue}
                          onValueChange={(value) => {
                            const newCols = [...(mapping.description as string[])]
                            newCols[idx + 1] = value === NONE_VALUE ? "" : value
                            onMappingChange("description", newCols)
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t("components.expenseImport.selectColumn")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_VALUE}>
                              {t("components.expenseImport.selectColumn")}
                              ...
                            </SelectItem>
                            {availableHeaders.map((header) => (
                              <SelectItem key={header} value={header}>
                                {header}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0"
                          onClick={() => {
                            const newCols = [...(mapping.description as string[])]
                            newCols.splice(idx + 1, 1)
                            onMappingChange("description", newCols)
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newCols = [
                        ...(Array.isArray(mapping.description)
                          ? mapping.description
                          : [mapping.description || ""]),
                        "",
                      ]
                      onMappingChange("description", newCols)
                    }}
                  >
                    + {t("components.expenseImport.addColumn")}
                  </Button>
                  <div className="space-y-2">
                    <Label className="text-xs">{t("components.expenseImport.separator")}</Label>
                    <Input
                      value={mapping.descriptionSeparator || " "}
                      onChange={(e) => onMappingChange("descriptionSeparator", e.target.value)}
                      placeholder={t("components.expenseImport.separator")}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("components.expenseImport.selectAmountColumn")} *</Label>
            <Select
              value={mapping.amount || ""}
              onValueChange={(value) => onMappingChange("amount", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("components.expenseImport.selectAmountColumn")} />
              </SelectTrigger>
              <SelectContent>
                {headers.map((header) => (
                  <SelectItem key={header} value={header}>
                    {header}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("components.expenseImport.selectCategoryColumn")}</Label>
            <Select
              value={
                Array.isArray(mapping.category)
                  ? mapping.category[0] || NONE_VALUE
                  : mapping.category || NONE_VALUE
              }
              onValueChange={(value) => onMappingChange("category", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("components.expenseImport.selectCategoryColumn")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>{t("common.none")}</SelectItem>
                {headers.map((header) => (
                  <SelectItem key={header} value={header}>
                    {header}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
